/**
 * @fileoverview Home V2 Component
 * Sprint 10: Buyer App 2.0 (AI + Personalization + Conversion Engine)
 * 
 * AI-driven, personalized homepage with:
 * - Personalized greeting
 * - AI recommendation panels
 * - Live market pulse
 * - Express vendors
 * - Quick access to AI Meal Planner
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  TrendingUp,
  Zap,
  Star,
  Clock,
  ChevronRight,
  ShoppingBag,
  Bike,
  Activity,
  Utensils,
  Search,
  Store,
  MapPin,
} from 'lucide-react';
import api from '../../lib/api';

// Types
interface MarketPulse {
  activeVendors: number;
  activeRiders: number;
  activeOrders: number;
  isPeakHour: boolean;
  peakStatus: 'low' | 'moderate' | 'busy' | 'very_busy';
  avgDeliveryTime: number;
  trending: { name: string; orderCount: number }[];
}

interface Vendor {
  id: string;
  name: string;
  description: string | null;
  logo_url: string | null;
  is_open: boolean;
  at_capacity: boolean;
  eta_minutes_band: string;
  delivery_fee_cents: number;
  reliability_score: number;
}

export default function HomeV2() {
  const navigate = useNavigate();
  const [marketPulse, setMarketPulse] = useState<MarketPulse | null>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [greeting, setGreeting] = useState({ text: '', emoji: '', subtitle: '' });

  // Generate time-based greeting
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) {
      setGreeting({ 
        text: 'Good Morning!', 
        emoji: '☀️', 
        subtitle: 'Start your day with fresh ingredients' 
      });
    } else if (hour < 17) {
      setGreeting({ 
        text: 'Good Afternoon!', 
        emoji: '🌤️', 
        subtitle: 'What would you like for merienda?' 
      });
    } else {
      setGreeting({ 
        text: 'Good Evening!', 
        emoji: '🌙', 
        subtitle: 'Plan a delicious dinner tonight' 
      });
    }
  }, []);

  // Fetch market pulse
  useEffect(() => {
    fetchMarketPulse();
    fetchVendors();
    const interval = setInterval(fetchMarketPulse, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchMarketPulse = async () => {
    try {
      const response = await api.get('/api/home/market-pulse');
      setMarketPulse(response.data);
    } catch (err) {
      console.error('Failed to fetch market pulse:', err);
    }
  };

  const fetchVendors = async () => {
    try {
      // Fetch all vendors (no zone filter for now - shows all active vendors)
      const response = await api.get('/api/buyer/vendors/search');
      setVendors(response.data.vendors || []);
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
    }
  };

  const getPeakStatusColor = (status: string) => {
    switch (status) {
      case 'busy':
      case 'very_busy':
        return 'text-orange-500 bg-orange-50';
      case 'moderate':
        return 'text-yellow-500 bg-yellow-50';
      default:
        return 'text-green-500 bg-green-50';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header with Greeting */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 text-white px-4 pt-6 pb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              {greeting.emoji} {greeting.text}
            </h1>
            <p className="text-green-100 text-sm mt-1">{greeting.subtitle}</p>
          </div>
        </div>

        {/* Search Bar */}
        <div 
          onClick={() => navigate('/search')}
          className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-white/30 transition"
        >
          <Search className="w-5 h-5 text-white/80" />
          <span className="text-white/80">Search products or vendors...</span>
        </div>
      </div>

      {/* AI Meal Planner Button - PROMINENT */}
      <div className="px-4 -mt-4 relative z-10">
        <button
          onClick={() => navigate('/ai/meal-planner')}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl p-4 shadow-lg flex items-center justify-between hover:shadow-xl transition transform hover:scale-[1.02]"
        >
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="text-left">
              <div className="font-bold text-lg">🍳 AI Meal Planner</div>
              <div className="text-white/80 text-sm">Plan your meals with AI assistant</div>
            </div>
          </div>
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Market Pulse */}
      {marketPulse && (
        <div className="px-4 mt-6">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-5 h-5 text-green-600" />
            <h2 className="font-semibold text-gray-800">Live Market Pulse</h2>
            <span className={`ml-auto text-xs px-2 py-1 rounded-full ${getPeakStatusColor(marketPulse.peakStatus)}`}>
              {marketPulse.peakStatus === 'very_busy' ? '🔥 Very Busy' : 
               marketPulse.peakStatus === 'busy' ? '⚡ Busy' :
               marketPulse.peakStatus === 'moderate' ? '📊 Moderate' : '✨ Low Traffic'}
            </span>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
                  <ShoppingBag className="w-4 h-4" />
                  <span className="font-bold text-xl">{marketPulse.activeVendors}</span>
                </div>
                <p className="text-xs text-gray-500">Vendors Online</p>
              </div>
              <div>
                <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
                  <Bike className="w-4 h-4" />
                  <span className="font-bold text-xl">{marketPulse.activeRiders}</span>
                </div>
                <p className="text-xs text-gray-500">Riders Ready</p>
              </div>
              <div>
                <div className="flex items-center justify-center gap-1 text-orange-600 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="font-bold text-xl">{marketPulse.avgDeliveryTime}</span>
                </div>
                <p className="text-xs text-gray-500">Avg. Minutes</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="px-4 mt-6">
        <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-500" />
          Quick Actions
        </h2>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => navigate('/search?q=ulam')}
            className="bg-white rounded-xl p-4 shadow-sm text-left hover:shadow-md transition"
          >
            <div className="text-2xl mb-2">🍖</div>
            <div className="font-medium text-gray-800">Ulam Ideas</div>
            <div className="text-xs text-gray-500">Main dishes</div>
          </button>
          <button
            onClick={() => navigate('/search?q=gulay')}
            className="bg-white rounded-xl p-4 shadow-sm text-left hover:shadow-md transition"
          >
            <div className="text-2xl mb-2">🥬</div>
            <div className="font-medium text-gray-800">Fresh Gulay</div>
            <div className="text-xs text-gray-500">Vegetables</div>
          </button>
          <button
            onClick={() => navigate('/search?q=merienda')}
            className="bg-white rounded-xl p-4 shadow-sm text-left hover:shadow-md transition"
          >
            <div className="text-2xl mb-2">🍰</div>
            <div className="font-medium text-gray-800">Merienda</div>
            <div className="text-xs text-gray-500">Snacks & treats</div>
          </button>
          <button
            onClick={() => navigate('/ai/meal-planner')}
            className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-4 text-left hover:shadow-md transition"
          >
            <div className="text-2xl mb-2">🤖</div>
            <div className="font-medium text-purple-700">Ask AI</div>
            <div className="text-xs text-purple-500">Get suggestions</div>
          </button>
        </div>
      </div>

      {/* Nearby Vendors */}
      {vendors.length > 0 && (
        <div className="px-4 mt-6">
          <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Store className="w-5 h-5 text-green-600" />
            Nearby Vendors
          </h2>
          <div className="space-y-3">
            {vendors.map((vendor) => (
              <div
                key={vendor.id}
                onClick={() => navigate(`/vendor/${vendor.id}`)}
                className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  {/* Vendor Avatar */}
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center flex-shrink-0">
                    {vendor.logo_url ? (
                      <img src={vendor.logo_url} alt={vendor.name} className="w-full h-full rounded-xl object-cover" />
                    ) : (
                      <Store className="w-8 h-8 text-green-600" />
                    )}
                  </div>
                  
                  {/* Vendor Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-800 truncate">{vendor.name}</h3>
                      {vendor.is_open ? (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Open</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">Closed</span>
                      )}
                    </div>
                    {vendor.description && (
                      <p className="text-sm text-gray-500 truncate mt-0.5">{vendor.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {vendor.eta_minutes_band} min
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        ₱{(vendor.delivery_fee_cents / 100).toFixed(0)} delivery
                      </span>
                      {vendor.reliability_score > 0 && (
                        <span className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-500" />
                          {vendor.reliability_score}%
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Popular Filipino Dishes */}
      <div className="px-4 mt-6">
        <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Utensils className="w-5 h-5 text-green-600" />
          Try Filipino Classics
        </h2>
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4">
          {['Adobo', 'Sinigang', 'Kare-Kare', 'Tinola', 'Pancit', 'Lechon'].map((dish) => (
            <button
              key={dish}
              onClick={() => navigate(`/search?q=${dish.toLowerCase()}`)}
              className="flex-shrink-0 bg-white rounded-xl px-4 py-3 shadow-sm hover:shadow-md transition flex items-center gap-2"
            >
              <span className="text-lg">
                {dish === 'Adobo' ? '🍗' : 
                 dish === 'Sinigang' ? '🍲' : 
                 dish === 'Kare-Kare' ? '🥘' :
                 dish === 'Tinola' ? '🍜' :
                 dish === 'Pancit' ? '🍝' : '🐖'}
              </span>
              <span className="font-medium text-gray-700">{dish}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Trending Items */}
      {marketPulse?.trending && marketPulse.trending.length > 0 && (
        <div className="px-4 mt-6">
          <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-red-500" />
            Trending Now
          </h2>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            {marketPulse.trending.slice(0, 5).map((item, index) => (
              <div
                key={item.name}
                onClick={() => navigate(`/search?q=${item.name}`)}
                className="flex items-center justify-between py-2 border-b last:border-0 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded"
              >
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 font-medium w-6">{index + 1}</span>
                  <span className="text-gray-800">{item.name}</span>
                </div>
                <span className="text-xs text-gray-500">{item.orderCount} orders</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer Spacing */}
      <div className="h-8" />
    </div>
  );
}

